import { inventory } from "@fm/api";
import type {
  InventoryGrid,
  InventoryGridRequest,
  UnavailableDateRange,
  UnavailableDateRangesRequest,
  ApiResponse,
} from "@fm/api";
import type { ApiClient } from "@fm/api/types";
import { getApiBaseUrl } from "../lib/config";

export interface UseInventoryOptions {
  apiKey: string;
  apiBaseUrl?: string;
}

export interface UseInventoryReturn {
  getInventoryGrid: (
    params: InventoryGridRequest,
  ) => Promise<ApiResponse<InventoryGrid>>;
  getUnavailableDateRanges: (
    params: UnavailableDateRangesRequest,
  ) => Promise<ApiResponse<UnavailableDateRange[]>>;
  checkAvailability: (params: {
    fromDate: string;
    toDate: string;
    vehicleId?: string;
    categoryId?: string;
  }) => Promise<{
    isAvailable: boolean;
    unavailableRanges: UnavailableDateRange[];
  }>;
}

export function useInventory(options: UseInventoryOptions): UseInventoryReturn {
  const { apiKey, apiBaseUrl } = options;

  // Create API client with the provided apiKey
  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  const getInventoryGrid = async (params: InventoryGridRequest) => {
    return inventory.getGrid(apiClient, params);
  };

  const getUnavailableDateRanges = async (
    params: UnavailableDateRangesRequest,
  ) => {
    return inventory.getUnavailableDateRanges(apiClient, params);
  };

  const checkAvailability = async (params: {
    fromDate: string;
    toDate: string;
    vehicleId?: string;
    categoryId?: string;
  }) => {
    const { fromDate, toDate, vehicleId, categoryId } = params;

    const unavailableRanges = await getUnavailableDateRanges({
      from_date: fromDate,
      to_date: toDate,
      vehicle_id: vehicleId,
      category_id: categoryId,
    });

    // Check if there are any unavailable ranges that overlap with the requested period
    const isAvailable =
      !unavailableRanges.data || unavailableRanges.data.length === 0;

    return {
      isAvailable,
      unavailableRanges: unavailableRanges.data || [],
    };
  };

  return {
    getInventoryGrid,
    getUnavailableDateRanges,
    checkAvailability,
  };
}
